<script setup lang="ts">
import { useUserStore } from '@/entities/user/store';
import { FIRST_MODERATION_STATUS_SUCCESS } from '@/shared/constants/moderationConstants';
import { MAX_COUNTER_VALUE } from '@/widgets/CabinetView/lib/cabinetTabBarConstants';
import type { CabinetTabBarLink } from '@/widgets/CabinetView/model/types/cabinetView';

defineOptions({ name: 'CabinetTabBar' });

const userStore = useUserStore();
const route = useRoute();

const isOpenedMenu: Ref<boolean> = ref(false);

const user = computed(() => userStore.user);

const isAuth: ComputedRef<boolean> = computed(() => userStore.isAuth);

const isShowPublications: ComputedRef<boolean> = computed(
  () =>
    userStore.isRoleTypeLawyer &&
    (user.value?.lawyer?.status ?? 0) >= FIRST_MODERATION_STATUS_SUCCESS,
);

const links: ComputedRef<CabinetTabBarLink[]> = computed(() =>
  [
    {
      id: 1,
      title: 'Чат',
      to: '/cabinet/chat/',
      iconName: 'ChatMenu',
      iconMod: '_chat',
      isShow: true,
      counter: userStore.unreadMessagesNumber,
    },
    {
      id: 2,
      title: 'Записи',
      to: '/cabinet/history/',
      iconName: 'AppointmentsMenu',
      iconMod: '_appointments',
      isShow: true,
      counter: user.value?.newOrdersCount,
    },
    {
      id: 3,
      title: 'Заявки',
      to: '/cabinet/callbacks/',
      iconName: 'CallbacksMenu',
      iconMod: '_callbacks',
      isShow: !userStore.isRoleTypeUser,
      counter: user.value?.newCallbacksCount,
    },
    {
      id: 4,
      title: 'Отзывы',
      to: '/cabinet/feedbacks/',
      iconName: 'ReviewsMenu',
      iconMod: '_reviews',
      isShow: true,
    },
    {
      id: 5,
      title: 'Избранное',
      to: '/cabinet/favorite/',
      iconName: 'FavoriteMenu',
      iconMod: '_favorite',
      isShow: userStore.isRoleTypeUser,
    },
  ].filter((link) => link.isShow),
);

const menuLinks: ComputedRef<CabinetTabBarLink[]> = computed(() =>
  [
    {
      id: 1,
      title: 'Настройки',
      to: '/cabinet/profile/',
      iconName: 'PersonProfile',
      iconMod: '_profile',
      isShow: true,
    },
    {
      id: 2,
      title: 'Статьи',
      to: '/cabinet/publications/',
      iconName: 'Publications',
      iconMod: '_publications',
      isShow: isShowPublications.value,
    },
    {
      id: 3,
      title: 'Избранное',
      to: '/cabinet/favorite/',
      iconName: 'FavoriteMenu',
      iconMod: '_favorite',
      isShow: !userStore.isRoleTypeUser,
    },
  ].filter((link) => link.isShow),
);

const isMenuMode: ComputedRef<boolean> = computed(() => !userStore.isRoleTypeUser);

const isActiveMenuLink: ComputedRef<boolean> = computed(() =>
  menuLinks.value.some((link) => route.path.startsWith(link.to)),
);

const splitIndex: ComputedRef<number> = computed(() => Math.ceil(links.value.length / 2));

const isOddRow: ComputedRef<boolean> = computed(() => links.value.length % 2 !== 0);

const formatCounter = (counter: number): string =>
  counter > MAX_COUNTER_VALUE ? `${MAX_COUNTER_VALUE}+` : String(counter);

const toggleMenu = (): void => {
  isOpenedMenu.value = !isOpenedMenu.value;
};

const closeMenu = (): void => {
  isOpenedMenu.value = false;
};

watch(() => route.fullPath, closeMenu);

const onKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'Escape') closeMenu();
};

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <div v-if="isAuth" class="cabinet-tab-bar" data-testid="cabinet-tab-bar">
    <Transition name="cabinet-tab-bar-fade">
      <div
        v-if="isOpenedMenu"
        class="cabinet-tab-bar__overlay"
        data-testid="cabinet-tab-bar-overlay"
        @click="closeMenu"
      ></div>
    </Transition>

    <Transition name="cabinet-tab-bar-menu">
      <div v-if="isOpenedMenu" class="cabinet-tab-bar__menu">
        <ul class="cabinet-tab-bar__menu-list">
          <li v-for="link in menuLinks" :key="link.id">
            <NuxtLink
              :to="link.to"
              active-class="_active"
              class="cabinet-tab-bar__menu-link"
              :data-testid="`cabinet-tab-bar-menu-${link.iconMod.slice(1)}`"
            >
              <AppIcon
                :class="['cabinet-tab-bar__menu-icon', link.iconMod]"
                :name="link.iconName"
              />
              {{ link.title }}
            </NuxtLink>
          </li>
        </ul>
      </div>
    </Transition>

    <nav class="cabinet-tab-bar__panel">
      <ul class="cabinet-tab-bar__list">
        <template v-for="(link, index) in links" :key="link.id">
          <li v-if="index === splitIndex" class="cabinet-tab-bar__item _fab">
            <button
              v-if="isMenuMode"
              type="button"
              :class="[
                'cabinet-tab-bar__fab',
                { _opened: isOpenedMenu, _active: isActiveMenuLink },
              ]"
              :aria-expanded="isOpenedMenu"
              aria-label="Ещё разделы"
              data-testid="cabinet-tab-bar-fab"
              @click="toggleMenu"
            >
              <AppIcon class="cabinet-tab-bar__fab-icon" name="PersonProfile" />
            </button>
            <NuxtLink
              v-else
              to="/cabinet/profile/"
              active-class="_active"
              class="cabinet-tab-bar__fab"
              aria-label="Настройки профиля"
              data-testid="cabinet-tab-bar-fab"
            >
              <AppIcon class="cabinet-tab-bar__fab-icon" name="PersonProfile" />
            </NuxtLink>
          </li>
          <li class="cabinet-tab-bar__item">
            <NuxtLink
              :to="link.to"
              active-class="_active"
              class="cabinet-tab-bar__link"
              :data-testid="`cabinet-tab-bar-${link.iconMod.slice(1)}`"
            >
              <span class="cabinet-tab-bar__icon-wrapper">
                <AppIcon
                  :class="['cabinet-tab-bar__icon', link.iconMod]"
                  :name="link.iconName"
                />
                <span
                  v-if="link.counter"
                  class="cabinet-tab-bar__counter"
                  :aria-label="`Новых: ${link.counter}`"
                >
                  {{ formatCounter(link.counter) }}
                </span>
              </span>
              <span class="cabinet-tab-bar__title">{{ link.title }}</span>
            </NuxtLink>
          </li>
        </template>
        <li v-if="isOddRow" class="cabinet-tab-bar__item" aria-hidden="true"></li>
      </ul>
    </nav>
  </div>
</template>

<style lang="less">
@tab-bar-max-width: 540px - (@size-x4 * 2);
@fab-size: 58px;
@fab-lift: 22px;
@fab-notch-gap: 5px;
@fab-notch-radius: (@fab-size / 2) + @fab-notch-gap;
@fab-notch-drop: 13px;

.cabinet-tab-bar {
  position: fixed;
  right: @size-x4;
  bottom: @size-x3;
  left: @size-x4;
  z-index: 102;
  max-width: @tab-bar-max-width;
  margin: 0 auto;
  padding-bottom: env(safe-area-inset-bottom);

  &__overlay {
    position: fixed;
    inset: 0;
    z-index: -1;
    background: rgba(21, 61, 73, 0.35);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
  }

  &__panel {
    position: relative;
    filter: drop-shadow(0 6px 16px rgba(21, 61, 73, 0.14))
      drop-shadow(0 16px 40px rgba(21, 61, 73, 0.12));
  }

  &__panel::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 20px;
    background: #fff;
    mask:
      radial-gradient(
          circle @fab-notch-radius at 50% @fab-notch-drop,
          transparent 99.5%,
          #000 100%
        )
        50% 0 / 100% 100% no-repeat,
      linear-gradient(#000, #000);
    mask-composite: intersect;
    -webkit-mask:
      radial-gradient(
          circle @fab-notch-radius at 50% @fab-notch-drop,
          transparent 99.5%,
          #000 100%
        )
        50% 0 / 100% 100% no-repeat,
      linear-gradient(#000, #000);
    -webkit-mask-composite: source-in;
  }

  &__list {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: stretch;
    padding: 6px @size-x2;
    gap: @main-size;
  }

  &__item {
    flex: 1 1 0;
    min-width: 0;

    &._fab {
      display: flex;
      justify-content: center;
      align-items: flex-start;
    }
  }

  &__link {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    border-radius: @size-x3;
    padding: 4px 2px;
    color: @color-elephant;
    transition: background-color @A;
  }

  &__link._active {
    background: rgba(@color-persimmon, 0.14);
  }

  &__icon-wrapper {
    position: relative;
    display: inline-flex;
  }

  &__icon {
    width: @size-x6;
    height: @size-x6;

    path {
      fill: @color-elephant;
      transition: fill @A;
    }

    &._callbacks path {
      stroke: @color-elephant;
      stroke-width: 0.2px;
    }
  }

  &__link._active &__icon {
    path {
      fill: @color-persimmon;
    }

    &._callbacks path {
      stroke: @color-persimmon;
    }
  }

  &__title {
    max-width: 100%;
    font-size: 11px;
    line-height: 13px;
    font-weight: 500;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__link._active &__title {
    font-weight: 700;
    color: @color-persimmon;
  }

  &__counter {
    position: absolute;
    top: -5px;
    right: -8px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    min-width: @size-x4;
    height: @size-x4;
    padding: 0 3px;
    border-radius: @size-x4;
    background-color: @color-crimson;
    color: #fff;
    font-size: 10px;
    line-height: 1;
    font-weight: 600;
  }

  &__fab {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    width: @fab-size;
    height: @fab-size;
    margin-top: -@fab-lift;
    border-radius: 50%;
    background: @color-persimmon;
    box-shadow: 0 4px 12px rgba(@color-persimmon, 0.35);
    transition: transform @A;

    &:active {
      transform: scale(0.94);
    }
  }

  &__fab-icon {
    width: @size-x6;
    height: @size-x6;

    path {
      fill: #fff;
    }
  }

  &__menu {
    position: absolute;
    right: 0;
    bottom: calc(100% + @size-x8);
    left: 0;
    padding: @size-x2;
    border-radius: @size-x5;
    background: #fff;
    box-shadow:
      0 6px 16px rgba(21, 61, 73, 0.14),
      0 16px 40px rgba(21, 61, 73, 0.12);
  }

  &__menu-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__menu-link {
    display: flex;
    align-items: center;
    gap: @size-x3;
    padding: @size-x3 @size-x4;
    border-radius: @size-x3;
    font-size: 14px;
    line-height: 18px;
    font-weight: 500;
    color: @color-elephant;
    transition: background-color @A;

    &._active {
      background: rgba(@color-persimmon, 0.14);
      font-weight: 700;
      color: @color-persimmon;
    }
  }

  &__menu-icon {
    width: @size-x5;
    height: @size-x5;
    flex-shrink: 0;

    path {
      fill: @color-elephant;
    }
  }

  &__menu-link._active &__menu-icon path {
    fill: @color-persimmon;
  }
}

.cabinet-tab-bar-fade-enter-active,
.cabinet-tab-bar-fade-leave-active {
  transition: opacity @A;
}

.cabinet-tab-bar-fade-enter-from,
.cabinet-tab-bar-fade-leave-to {
  opacity: 0;
}

.cabinet-tab-bar-menu-enter-active,
.cabinet-tab-bar-menu-leave-active {
  transition:
    opacity @A,
    transform @A;
}

.cabinet-tab-bar-menu-enter-from,
.cabinet-tab-bar-menu-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
